/**
 * Where the conversation changed subject, shown in the transcript at the
 * point it happened.
 *
 * Sticky within its section rather than the whole list: each marker holds the
 * top of the window for as long as the stretch it opened, then the next
 * section's marker pushes it out. That only works while the marker and the
 * messages it covers share a containing block — see `groupMessagesByContext`.
 *
 * Nothing here is interactive. A marker is something that happened, not
 * something to act on.
 */

import {
  BookOpen,
  Box,
  FileText,
  Images,
  MessageCircle,
  PauseCircle,
  Workflow,
} from 'lucide-react';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { contextSwitchParts, type ContextSwitch } from './context-switch';

const ICONS = {
  artifact: FileText,
  skill_doc: BookOpen,
  artifact_gallery: Images,
  workflow_canvas: Workflow,
  workflow_node: Box,
  node_chat: PauseCircle,
  none: MessageCircle,
} as const;

interface ContextSwitchMarkerProps {
  context: ContextSwitch;
  className?: string;
}

export function ContextSwitchMarker({
  context,
  className,
}: ContextSwitchMarkerProps) {
  const Icon = ICONS[context.kind];
  const { lead, name } = contextSwitchParts(context);

  return (
    <div
      role='status'
      className={cn(
        // Opaque, and the same ground as the chat panel: this exists only so
        // the conversation does not show through while the marker is pinned,
        // so any other colour reads as a band across the thread.
        'sticky top-0 z-10 -mx-4 flex flex-col gap-3 bg-sidebar px-4',
        className,
      )}
    >
      <Separator />
      <div className='flex min-w-0 items-center gap-2'>
        <Icon className='size-4 shrink-0 text-muted-foreground' />
        <p className='min-w-0 flex-1 truncate text-sm font-medium'>
          <span className='text-muted-foreground'>{lead}</span>
          {name && <span className='text-foreground'>{` “${name}”`}</span>}
        </p>
      </div>
      <Separator />
    </div>
  );
}
